import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Business, Search, Warning, Assignment, Event } from "@mui/icons-material";
import { useSites } from "@/api/hooks/sites";
import { formatSiteDisplayId } from "@/api/labels";
import {
  PIPELINE_STAGES,
  PIPELINE_STAGE_LABELS,
  TARGET_TIERS,
  TARGET_TIER_LABELS,
} from "@/api/labels";
import type { PipelineStage, TargetTier } from "@/api/types";

const STAGE_COLOR: Record<
  PipelineStage,
  "info" | "primary" | "warning" | "success" | "default"
> = {
  qualified: "primary",
  contracting: "warning",
  activated: "success",
  declined: "default",
};

const TIER_COLOR: Record<TargetTier, "error" | "warning" | "info" | "default"> = {
  tier_1: "error",
  tier_2: "warning",
  tier_3: "info",
  not_targeted: "default",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function SitesList() {
  const { data, isLoading, error } = useSites();
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("");
  const [tierFilter, setTierFilter] = useState<string>("");

  const filtered = useMemo(() => {
    const rows = data ?? [];
    return rows.filter((s) => {
      if (search && !s.common_name.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      if (stageFilter && s.pipeline_stage !== stageFilter) return false;
      if (tierFilter && s.target_tier !== tierFilter) return false;
      return true;
    });
  }, [data, search, stageFilter, tierFilter]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5">Sites</Typography>
          <Typography variant="body2" color="text.secondary">
            {isLoading
              ? "Loading…"
              : `${filtered.length} site${filtered.length !== 1 ? "s" : ""}`}
          </Typography>
        </Box>
        <Link to="/clinops/sites/new" style={{ textDecoration: "none" }}>
          <Button variant="contained" startIcon={<Add />}>
            New site
          </Button>
        </Link>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          size="small"
          select
          label="Pipeline stage"
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All stages</MenuItem>
          {PIPELINE_STAGES.map((s) => (
            <MenuItem key={s} value={s}>
              {PIPELINE_STAGE_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Target tier"
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All tiers</MenuItem>
          {TARGET_TIERS.map((t) => (
            <MenuItem key={t} value={t}>
              {TARGET_TIER_LABELS[t]}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load sites: {error.message}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 80 }}>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell align="right">
                <Assignment
                  fontSize="inherit"
                  sx={{ verticalAlign: "middle", mr: 0.5 }}
                />
                Open actions
              </TableCell>
              <TableCell align="right">
                <Warning
                  fontSize="inherit"
                  sx={{ verticalAlign: "middle", mr: 0.5 }}
                />
                High-sev risks
              </TableCell>
              <TableCell>
                <Event
                  fontSize="inherit"
                  sx={{ verticalAlign: "middle", mr: 0.5 }}
                />
                Next key date
              </TableCell>
              <TableCell>Last transcript</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  {data && data.length === 0
                    ? "No sites yet. Create one to start tracking onboarding."
                    : "No sites match the current filters."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace", color: "text.secondary" }}>
                    {formatSiteDisplayId(s.display_id)}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/clinops/sites/${s.id}`}
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontWeight: 500,
                      }}
                    >
                      <Business fontSize="small" color="disabled" />
                      {s.common_name}
                    </Link>
                    {s.legal_entity_name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", pl: 3 }}
                      >
                        {s.legal_entity_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={PIPELINE_STAGE_LABELS[s.pipeline_stage]}
                      color={STAGE_COLOR[s.pipeline_stage]}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={TARGET_TIER_LABELS[s.target_tier]}
                      color={TIER_COLOR[s.target_tier]}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {s.open_action_items > 0 ? (
                      <strong>{s.open_action_items}</strong>
                    ) : (
                      <Typography component="span" color="text.disabled">0</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {s.high_severity_open_risks > 0 ? (
                      <Typography color="error.main" sx={{ fontWeight: 600 }}>
                        {s.high_severity_open_risks}
                      </Typography>
                    ) : (
                      <Typography component="span" color="text.disabled">0</Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(s.next_key_date)}</TableCell>
                  <TableCell>{formatDate(s.last_transcript_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
